import { useTranslation } from 'react-i18next';
import { ArrowLeft, Package } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/atoms/avatar';
import { MessageList } from './message-list';
import { MessageInput } from './message-input';
import type { Conversation, Message } from '@/lib/types/messaging';

interface ConversationDetailProps {
  conversation: Conversation | null;
  messages: Message[];
  isLoading?: boolean;
  isLoadingMessages?: boolean;
  isLoadingMore?: boolean;
  isSending?: boolean;
  hasNextPage?: boolean;
  onSendText: (text: string) => void;
  onSendImage?: (file: File) => void;
  onLoadMore?: () => void;
  onImageClick?: (imageUrl: string) => void;
  onAudioPlay?: (audioUrl: string) => void;
  onBack?: () => void;
  showBackButton?: boolean;
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; dotColor: string }> = {
  active: { label: 'Actif', variant: 'default', dotColor: 'bg-green-500' },
  disputed: { label: 'Litige', variant: 'destructive', dotColor: 'bg-red-500' },
  resolved: { label: 'Résolu', variant: 'secondary', dotColor: 'bg-blue-500' },
  closed: { label: 'Fermé', variant: 'outline', dotColor: 'bg-gray-400' },
  expired: { label: 'Expiré', variant: 'outline', dotColor: 'bg-gray-400' },
};

export function ConversationDetail({
  conversation,
  messages,
  isLoading = false,
  isLoadingMessages = false,
  isLoadingMore = false,
  isSending = false,
  hasNextPage = false,
  onSendText,
  onSendImage,
  onLoadMore,
  onImageClick,
  onAudioPlay,
  onBack,
  showBackButton = false,
}: ConversationDetailProps) {
  const { t } = useTranslation();
  const canSendMessages = conversation?.canSendMessages ?? false;
  const status = conversation ? statusConfig[conversation.status] : null;

  if (!conversation && !isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <p className="text-muted-foreground">
          {t('messaging.selectConversation', 'Sélectionnez une conversation')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background rounded-lg border overflow-hidden">
      {conversation && (
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-gradient-to-r from-muted/50 to-muted/20">
          {showBackButton && onBack && (
            <Button variant="ghost" size="icon-sm" onClick={onBack} className="shrink-0">
              <ArrowLeft className="size-4" />
            </Button>
          )}

          {/* Customer Avatar */}
          <Avatar className="size-10 shrink-0 ring-2 ring-background shadow-sm">
            <AvatarImage src={conversation.customer.avatarUrl ?? undefined} alt={conversation.customer.name} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium text-sm">
              {getInitials(conversation.customer.name)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold truncate text-foreground">
                {conversation.customer.name}
              </span>
              {status && (
                <Badge
                  variant={status.variant}
                  className={cn(
                    'text-[10px] px-2 py-0 h-5 font-medium',
                    status.variant === 'default' && 'bg-green-500/10 text-green-600 border-green-200'
                  )}
                >
                  <span className={cn('size-1.5 rounded-full mr-1.5', status.dotColor)} />
                  {status.label}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Package className="size-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Commande #{conversation.order.orderNumber}
              </p>
            </div>
          </div>
        </div>
      )}

      <MessageList
        messages={messages}
        isLoading={isLoadingMessages}
        isLoadingMore={isLoadingMore}
        hasNextPage={hasNextPage}
        onLoadMore={onLoadMore}
        onImageClick={onImageClick}
        onAudioPlay={onAudioPlay}
      />

      {canSendMessages ? (
        <MessageInput
          onSendText={onSendText}
          onSendImage={onSendImage}
          isLoading={isSending}
          disabled={!canSendMessages}
        />
      ) : (
        <div className="p-3 border-t bg-muted/30">
          <Alert>
            <AlertDescription className="text-sm">
              {t('messaging.conversationClosed', 'Cette conversation est fermée')}
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}
